/**
 * ProposalsBoardConfig — column definitions + bucketing rules for the
 * CMB Proposals workflow (kanban) view.
 *
 * Mirrors the POS Proposals shape but without the Awaiting Review
 * column — CMB has no admin-approval step today. If operators-prepare /
 * managers-approve lands later, an Awaiting Review column slots in
 * between Drafting and Sent without churn.
 *
 * Bucketing is auto-derived from status + viewed_at + converted_to_booking_id
 * + expires_at. No `workflow_stage` column on estimates.
 */

export interface WorkflowColumn {
  id: string;
  label: string;
  color: string;       // top border on the column header
  helpText: string;    // shown in empty state
}

export const WORKFLOW_COLUMNS: WorkflowColumn[] = [
  {
    id: 'drafting',
    label: 'Drafting',
    color: 'var(--color-text-light)',
    helpText: 'Drafts in progress. Send via the proposal link to move to Sent.',
  },
  {
    id: 'sent',
    label: 'Sent',
    color: 'var(--color-info)',
    helpText: 'Shared with the customer but not opened yet.',
  },
  {
    id: 'engaged',
    label: 'Engaged',
    color: 'var(--color-primary-light)',
    helpText: 'Customer has viewed in the last 7 days. Stay close.',
  },
  {
    id: 'stalled',
    label: 'Stalled',
    color: 'var(--color-error)',
    helpText: 'No views in 7+ days. Needs follow-up.',
  },
  {
    id: 'won',
    label: 'Won',
    color: 'var(--color-success)',
    helpText: 'Converted to a booking. Hand off to delivery.',
  },
  {
    id: 'lost',
    label: 'Lost',
    color: 'var(--color-text-light)',
    helpText: 'Expired without conversion. Re-quote candidates after a cooling period.',
  },
];

/** Days of inactivity before a viewed proposal counts as Stalled. */
export const STALLED_THRESHOLD_DAYS = 7;

interface BucketableEstimate {
  status?: string | null;
  viewed_at?: string | null;
  sent_at?: string | null;
  converted_to_booking_id?: string | null;
  expires_at?: string | null;
  [key: string]: unknown;
}

/**
 * Map an estimate to its workflow column id. Pure function.
 *
 * Precedence:
 *   1. Won (terminal): converted to booking OR explicit accepted status
 *   2. Lost (terminal): explicit expired status OR past expires_at
 *   3. Drafting: status = 'draft'
 *   4. Engaged / Stalled: derived from viewed_at recency
 *   5. Sent: anything else (sent but not yet viewed)
 */
export function bucketProposal(p: BucketableEstimate): string {
  if (p.converted_to_booking_id || p.status === 'accepted') return 'won';
  if (p.status === 'expired' || (p.expires_at && new Date(p.expires_at).getTime() < Date.now())) {
    return 'lost';
  }
  if (p.status === 'draft') return 'drafting';

  if (p.viewed_at) {
    const daysSinceView = (Date.now() - new Date(p.viewed_at).getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceView <= STALLED_THRESHOLD_DAYS ? 'engaged' : 'stalled';
  }

  return 'sent';
}

/**
 * Group an array of estimates by workflow column id.
 * Returns a Record keyed by column id, with empty arrays for columns
 * that have no estimates so the UI always renders all 6.
 */
export function groupProposalsByColumn<T extends BucketableEstimate>(
  proposals: T[],
): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};
  WORKFLOW_COLUMNS.forEach((col) => { grouped[col.id] = []; });
  proposals.forEach((p) => {
    const colId = bucketProposal(p);
    (grouped[colId] ||= []).push(p);
  });
  return grouped;
}
